import {
  calculateProgress,
  formatCartMilestoneCurrency,
  type Currency,
  type DiscountTierResponse,
  type ProgressBarResponse,
} from '@skin-seoul/service';

export interface CartBenefit {
  id: string;
  title: string;
  label: string;
  minAmount: number;
  currentAmount: number;
  remaining: number;
  available: boolean;
  applied: boolean;
}

// Figma's three milestone centers on the 303px rail; the final milestone ends the rail.
const THREE_TIER_ANCHORS = [(49.5 / 303) * 100, (158.5 / 303) * 100, 100];

export function getCartBenefits(progressBar: ProgressBarResponse | null, currency: Currency) {
  const benefits: CartBenefit[] = [];
  if (progressBar?.freeShipping) {
    benefits.push({ ...progressBar.freeShipping, id: 'shipping', title: 'Free Shipping', label: 'Free Ship' });
  }
  const discountTiers = [...(progressBar?.discountTiers ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);
  const reachedFixedDiscount = discountTiers
    .filter(
      (tier): tier is Extract<DiscountTierResponse, { discountType: 'fixed_cart' }> =>
        tier.available && tier.discountType === 'fixed_cart',
    )
    .reduce((maximum, tier) => Math.max(maximum, tier.discountAmount), 0);
  for (const tier of discountTiers) {
    const title =
      tier.discountType === 'fixed_cart'
        ? `${formatCartMilestoneCurrency(currency, tier.discountAmount)} OFF`
        : `${tier.percentage}% OFF`;
    const headline =
      !tier.available &&
      tier.discountType === 'fixed_cart' &&
      reachedFixedDiscount > 0 &&
      tier.discountAmount > reachedFixedDiscount
        ? `+${formatCartMilestoneCurrency(currency, tier.discountAmount - reachedFixedDiscount)} OFF`
        : title;
    benefits.push({ ...tier, id: `discount:${tier.couponCode}`, title: headline, label: title });
  }
  const anchors =
    benefits.length === THREE_TIER_ANCHORS.length
      ? THREE_TIER_ANCHORS
      : benefits.map((_, index) => ((index + 1) / benefits.length) * 100);
  const campaignKey = JSON.stringify(benefits.map(({ id, minAmount, label }) => [id, minAmount, label]));
  return { benefits, anchors, campaignKey, progress: calculateProgress({ tiers: benefits, anchors }) };
}

export function hasAllCartRewards(benefits: CartBenefit[]) {
  return benefits.length > 0 && benefits.every(({ available }) => available);
}
